"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { pxToVw } from "@/utils/pxToVw";

import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, A11y } from "swiper/modules";
import { MOBILE_BASE } from "@/theme/theme";
import { useText } from "@/context/TextContext";

type Testimonial = {
  id: string;
  alt: string;
  image: string;
};

type Props = {
  testimonials: Testimonial[];
};

export default function ProductesCarousel({ testimonials }: Props) {
  const swiperRef = useRef<SwiperType | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Grid container mb={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(160) }}>
      {/* Header + flechas arriba derecha */}
      <Grid
        size={12}
        container
        mb={{ xs: pxToVw(60, MOBILE_BASE), md: pxToVw(60) }}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography
          variant="titulo"
          color="marron"
          mb={{ xs: pxToVw(6, MOBILE_BASE), md: pxToVw(10) }}
          sx={{ textTransform: "uppercase" }}
        >
          {useText("lloguer.confiat.title")}
        </Typography>
        <Typography
          variant="body2"
          color="marron"
          fontSize={{ xs: pxToVw(16, MOBILE_BASE), md: pxToVw(30) }}
          lineHeight={{ xs: "115%", md: "110%" }}
        >
          {useText("lloguer.confiat.text")}
        </Typography>
      </Grid>

      {!isMobile && (
        <>
          <Grid container size={0.5}>
            <IconButton
              aria-label="anterior"
              onClick={() => swiperRef.current?.slidePrev()}
              sx={{
                fontSize: pxToVw(16),
                color: "#472600",
                padding: 0,
                fontFamily: "RomekSharp",
                ":hover": { backgroundColor: "transparent" },
              }}
            >
              ←
            </IconButton>
          </Grid>
          <Grid container size={11}>
            {/* Swiper */}
            <Swiper
              modules={[Navigation, A11y]}
              onSwiper={(s) => (swiperRef.current = s)}
              slidesPerView={6}
              spaceBetween={0}
              speed={500}
              loop={true}
              navigation={true}
              style={{ width: "100%" }}
            >
              {testimonials.map((p) => (
                <SwiperSlide key={p.id}>
                  <Card
                    elevation={0}
                    sx={{
                      boxShadow: "none",
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                      height: pxToVw(45),
                    }}
                  >
                    {/* Imagen del producto */}
                    <Image
                      src={p.image}
                      alt={p.alt}
                      width={640}
                      height={480}
                      style={{
                        width: "100%",
                        height: "100%",
                        //height: pxToVw(43),
                        objectFit: "contain",
                      }}
                      priority={false}
                      className="imagen-producto-carousel"
                    />
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </Grid>
          <Grid container size={0.5} justifyContent={"flex-end"}>
            <IconButton
              aria-label="siguiente"
              onClick={() => swiperRef.current?.slideNext()}
              sx={{
                fontSize: pxToVw(16),
                color: "#472600",
                padding: 0,
                fontFamily: "RomekSharp",
                ":hover": { backgroundColor: "transparent" },
              }}
            >
              →
            </IconButton>
          </Grid>
        </>
      )}
      {isMobile && (
        <>
          <Grid container size={2}>
            <IconButton
              aria-label="anterior"
              onClick={() => swiperRef.current?.slidePrev()}
              sx={{
                fontSize: pxToVw(25, MOBILE_BASE),
                color: "#472600",
                padding: 0,
                fontFamily: "RomekSharp",
                ":hover": { backgroundColor: "transparent" },
              }}
            >
              ←
            </IconButton>
          </Grid>
          <Grid container size={8}>
            {/* Swiper */}
            <Swiper
              modules={[Navigation, A11y]}
              onSwiper={(s) => (swiperRef.current = s)}
              slidesPerView={2}
              spaceBetween={50}
              speed={500}
              loop={true}
              navigation={true}
              style={{ width: "100%" }}
            >
              {testimonials.map((p) => (
                <SwiperSlide key={p.id}>
                  <Card
                    elevation={0}
                    sx={{
                      boxShadow: "none",
                      alignItems: "center",
                      justifyContent: "center",
                      display: "flex",
                      height: pxToVw(30, MOBILE_BASE),
                    }}
                  >
                    {/* Imagen del producto */}
                    <Image
                      src={p.image}
                      alt={p.alt}
                      width={93}
                      height={30}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      priority={false}
                    />
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </Grid>
          <Grid container size={2} justifyContent={"flex-end"}>
            <IconButton
              aria-label="siguiente"
              onClick={() => swiperRef.current?.slideNext()}
              sx={{
                fontSize: pxToVw(25, MOBILE_BASE),
                color: "#472600",
                padding: 0,
                fontFamily: "RomekSharp",
                ":hover": { backgroundColor: "transparent" },
              }}
            >
              →
            </IconButton>
          </Grid>
        </>
      )}
    </Grid>
  );
}
